import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useResource } from '../hooks/useApi.js';

/**
 * The compact setup nudge, shown in the sidebar and on the dashboard.
 *
 * It counts only the steps that actually block getting paid. The optional ones
 * — API keys, webhooks, a team — are real work but they are not *missing*, and
 * a shop that will only ever use payment links and the till should not be
 * chased forever for an API key it does not want.
 *
 * Disappears once the required steps are done, or once it is dismissed.
 */
export function OnboardingChecklist({ compact = false }) {
  const { data } = useResource('/dashboard/setup');

  if (!data || data.complete || data.dismissed) return null;

  const pct = (data.requiredDone / data.requiredTotal) * 100;
  const next = data.steps.find((s) => !s.done && s.required && s.href);

  if (compact) {
    return (
      <div className="rounded-lg border p-3" style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="eyebrow">Setup</span>
          <span className="tabular text-[11px] font-medium text-muted">
            {data.requiredDone}/{data.requiredTotal}
          </span>
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full" style={{ background: 'rgb(var(--line))' }}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${pct}%`, background: 'rgb(var(--ink))' }}
          />
        </div>
        <Link
          to="/setup"
          className="mt-2.5 flex items-center gap-1 text-xs font-medium text-accent-ink hover:underline"
        >
          {next ? next.title : 'Finish setting up'} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  return (
    <div className="panel overflow-hidden">
      <div
        className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4"
        style={{ borderColor: 'rgb(var(--line-soft))' }}
      >
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-ink">
            {data.readyToTakePayments ? 'Nearly there' : 'Let’s get you paid'}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            {data.requiredDone} of {data.requiredTotal} things you need before a customer can pay you.
          </p>
        </div>
        <Link to="/setup" className="btn btn-ghost btn-sm">
          Open setup <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="divide-y" style={{ borderColor: 'rgb(var(--line-soft))' }}>
        {data.steps.filter((s) => s.required).map((step) => (
          <div key={step.key} className="flex items-center gap-3 px-5 py-3">
            <StepDot done={step.done} />
            <span className={`flex-1 text-sm ${step.done ? 'text-muted line-through' : 'text-ink'}`}>
              {step.title}
            </span>
            {!step.done && step.href && (
              <Link to={step.href} className="text-xs font-medium text-accent-ink hover:underline">
                {step.cta ?? 'Do it'}
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function StepDot({ done }) {
  return (
    <span
      className="grid h-5 w-5 shrink-0 place-items-center rounded-full border text-white"
      style={{
        borderColor: done ? 'rgb(var(--ink))' : 'rgb(var(--line))',
        background: done ? 'rgb(var(--ink))' : 'transparent',
      }}
      aria-hidden="true"
    >
      {done && (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 6.5L5 9l4.5-5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

export default OnboardingChecklist;
